<template>
  <div ref="tableContainer" class="table-container">
      <el-table
        ref="multipleTable"
        :class="[
          showSelection ? 'select_container' : '', 
          hasTotalData ? 'has_total_data' : '',
        ]"
        :data="tableData"
        tooltip-effect="dark"
        :style="{width: '100%', ...tableStyle}"
        size="medium"
        :border="true"
        :height="height"
        :max-height="maxHeight"
        :header-cell-style="{background:'#f5f7fa'}"
        v-loading="loading"
        :row-key="rowKey"
        :tree-props="rowKey ? treeProps : null"
        :default-expand-all="defaultExpandAll"
        :indent="indent"
        :lazy="lazy"
        @selection-change="handleSelectionChange"
        @expand-change="handleExpandChange"
        @sort-change="handleSortChange"
      >
        <!-- 多选框 -->
        <el-table-column 
          v-if="showSelection"
          type="selection" 
          fixed 
          width="55" 
          :selectable="selectable"
          :reserve-selection="true"
        ></el-table-column>
    
        <!-- 动态列 -->
        <template v-for="(column, index) in columns">
          <el-table-column
            :key="column.prop + index"
            :prop="column.prop"
            :label="column.label"
            :width="column.width"
            :fixed="column.fixed"
            :min-width="column.minWidth"
            :align="column.align || 'center'"
            :sortable="column.sortable || false"
            :show-overflow-tooltip="column.showOverflowTooltip || false"
          >
            <!-- 自定义表头 -->
            <template slot="header" slot-scope="scope">
              <slot :name="column.headerSlotKey" :scope="scope" :column="column">
                {{ column.label || '' }}
              </slot>
            </template>

            <template slot-scope="scope">
              <slot :name="column.slotKey" :scope="scope" :value="scope.row[column.prop] || scope.row[column.default] || ''">
                <!-- 当渲染的列有子属性时，使用子属性渲染 -->
                 <template v-if="column.subProp">
                  {{ scope.row[column.prop] && scope.row[column.prop][column.subProp] ? scope.row[column.prop][column.subProp] : scope.row[column.default] || '' }}
                 </template>
                 <template v-else>
                  {{ scope.row[column.prop] || scope.row[column.default] || '' }}
                 </template>
              </slot>
            </template>
          </el-table-column>
        </template>
    
        <!-- 操作列插槽 -->
        <slot name="operation"></slot>
      </el-table>
  
      <!-- 分页组件 -->
      <pagination
        v-show="showPagination && total > 0"
        :total="total"
        :page.sync="innerPageNo"
        :limit.sync="innerPageSize"
        :pageSizes="pageSizesList"
        @pagination="handlePaginationChange"
      />
    </div>
  </template>
  
  <script>
  
  export default {
    /**
     * @function {clearSelection} 清空多选
     */
    name: 'MyTable',
    props: {
      // 表格数据
      tableData: {
        type: Array,
        default: () => []
      },
      // 列配置
      columns: {
        type: Array,
        required: true
      },
      // 是否有汇总数据
      hasTotalData:{
        type: Boolean,
        default: false
      },
      // 是否显示多选框
      showSelection: {
        type: Boolean,
        default: false
      },
      tableStyle:{
        type: Object,
        default: () => ({})
      },
      height:{
        type: [String, Number],
        default: null
      },
      maxHeight:{
        type: [String, Number],
        default: null
      },
      // 总条数
      total: {
        type: Number,
        default: 0
      },
      // 行key
      rowKey: {
        type: String,
        default: 'id'
      },
      // 加载状态
      loading: {
        type: Boolean,
        default: false
      },
      // 多选相关：可选函数，控制多选框是否禁用
      selectable: {
        type: Function,
        default: () => true
      },
      // 表格是否可拖拽滚动是否可拖拽
      canDrag:{
        type: Boolean,
        default: true
      },
      treeProps:{
        type: Object,
        default: () => {return { hasChildren: 'hasChildren', children: 'children' }}
      },
      // 是否默认展开所有行，当 Table 包含展开行存在或者为树形表格时有效
      defaultExpandAll:{
        type: Boolean,
        default: false
      },
      lazy:{
        type: Boolean,
        default: false
      },
      indent:{
        type: Number,
        default: 16
      },


      /** 分页器相关 */
      // 是否显示分页
      showPagination: {
        type: Boolean,
        default: true
      },
      // 当前页码
      pageNo: {
        type: Number,
        default: 1
      },
      // 每页显示条数
      pageSize: {
        type: Number,
        default: 10
      },
      // 每页显示个数选择器的选项设置
      pageSizesList: {
        type: Array,
        default: ()=>[10, 20, 50, 100]
      },
    },
    data() {
      return {
        innerPageNo: this.pageNo,
        innerPageSize: this.pageSize
      }
    },
    watch: {
      pageNo(newVal) {
        this.innerPageNo = newVal
      },
      pageSize(newVal) {
        this.innerPageSize = newVal
      }
    },
    mounted() {
      // 当开启拖拽或者列数大于6时，启用拖拽
      if(this.canDrag && this.columns.length > 6) {
        this.enableDrag();
      }
    },
    methods: {
      getTableDom(){
        return this.$refs.multipleTable
      },
      /** 清空多选 */
      clearSelection() {
        this.$refs.multipleTable.clearSelection()
      },
      handleSelectionChange(selection) {
        this.$emit('selection-change', selection)
      },
      handlePaginationChange({ page, limit }) {
        this.innerPageNo = page
        this.innerPageSize = limit
        this.$emit('pagination-change', {
          pageNo: page,
          pageSize: limit
        })
      },
      handleExpandChange(row, expandedRows){
        this.$emit('expand-change', row, expandedRows)
      },
      handleSortChange(sort){
        this.$emit('sort-change', sort)
      },
      clearSort(){
        this.$refs.multipleTable.clearSort()
      },
      /**鼠标左右拖拽移动表格
       * 鼠标点击进行拖拽，首先想到鼠标的点击事件，添加mousedown、mouseleave、mouseup和mousemove事件的监听器，实现拖拽效果。通过设置tableBodyWrapper.style.overflow = 'hidden';隐藏原生的滚动条
       */
      enableDrag() {
        this.$nextTick(() => {
          if (!this.canDrag) return;

          const tableBodyWrapper = this.$refs.multipleTable.$el.querySelector('.el-table__body-wrapper');
          if (!tableBodyWrapper) {
            console.error('找不到表体。');
            return;
          }

          let isDown = false;
          let startX, scrollLeft;
          let ctrlKeyPressed = false; // 跟踪Ctrl键状态

          // 禁止表格内的文字选择
          const disableTableTextSelection = () => {
            tableBodyWrapper.style.userSelect = 'none';
            tableBodyWrapper.style.webkitUserSelect = 'none';
          };

          // 恢复表格内的文字选择
          const enableTableTextSelection = () => {
            tableBodyWrapper.style.userSelect = '';
            tableBodyWrapper.style.webkitUserSelect = '';
          };

          // 监听全局Ctrl键按下状态
          const handleKeyDown = (e) => {
            if (e.key === 'Control' || e.key === 'Meta') { // Meta键对应Mac上的Command键
              ctrlKeyPressed = true;
            }
          };

          const handleKeyUp = (e) => {
            if (e.key === 'Control' || e.key === 'Meta') {
              ctrlKeyPressed = false;
              // 如果正在拖拽但松开Ctrl键，则停止拖拽
              if (isDown) {
                isDown = false;
                tableBodyWrapper.style.cursor = '';
                enableTableTextSelection();
              }
            }
          };

          // 添加全局键盘事件监听
          window.addEventListener('keydown', handleKeyDown);
          window.addEventListener('keyup', handleKeyUp);

          tableBodyWrapper.addEventListener('mousedown', (e) => {
            if (e.button !== 0 || !ctrlKeyPressed) return; // 仅左键且Ctrl键按下时触发拖拽
            isDown = true;
            startX = e.pageX - tableBodyWrapper.offsetLeft;
            scrollLeft = tableBodyWrapper.scrollLeft;
            tableBodyWrapper.style.cursor = 'grabbing';
            disableTableTextSelection();
          });

          tableBodyWrapper.addEventListener('mouseleave', () => {
            if (!isDown) return;
            isDown = false;
            tableBodyWrapper.style.cursor = '';
            enableTableTextSelection();
          });

          tableBodyWrapper.addEventListener('mouseup', () => {
            if (!isDown) return;
            isDown = false;
            tableBodyWrapper.style.cursor = '';
            enableTableTextSelection();
          });

          tableBodyWrapper.addEventListener('mousemove', (e) => {
            if (!isDown || !ctrlKeyPressed) return; // 只有按下Ctrl键时才执行拖拽
            e.preventDefault();
            const x = e.pageX - tableBodyWrapper.offsetLeft;
            const walk = (x - startX) * 2;
            tableBodyWrapper.scrollLeft = scrollLeft - walk;
          });

          // 组件销毁时清理
          this.$once('hook:beforeDestroy', () => {
            enableTableTextSelection();
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
          });
        });
      },
    }
  }
  </script>
  
  <style lang="scss" scoped>
  .table-container {
    position: relative;
    min-height: 100px;
  }

.select_container{
    ::v-deep .el-table__fixed, .el-table__fixed-right {
        .el-table__fixed-header-wrapper{
            pointer-events: auto;
        }
    }
}
.has_total_data{
  ::v-deep .el-table__row{
    &:first-child{
      background-color: #f5f7fa ;
    }
  }
}
  </style>